import React, { useState, useRef, useEffect } from 'react';
import { ListGroup, Button, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faEdit, faSearch, faFileExport } from '@fortawesome/free-solid-svg-icons';
import { MapCardContainer } from './MapCardContainer';
import { useGlobalLynxClient } from '@iotopen/react-lynx';
import { DeviceFunctions } from './DeviceFunctions';
import { UpdateDeviceModal } from './Modals/UpdateDeviceModal';
import { Devicex, Functionx } from '@iotopen/node-lynx';
import * as XLSX from 'xlsx';

interface ItemListProps {
  items: ScannedItem[];
  onUpdateLocation: (id: string, latitude: number | null, longitude: number | null) => void;
  onUpdateName: (id: string, name: string) => void;
  installationId: number;
  selectedItem: string | null;
  setSelectedItem: React.Dispatch<React.SetStateAction<string | null>>;
  newName: string;
  setNewName: React.Dispatch<React.SetStateAction<string>>;
  comment: string;
  setComment: React.Dispatch<React.SetStateAction<string>>;
  showDeviceModal: boolean;
  setShowDeviceModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const capitalizeFirstLetter = (str: string | undefined) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
};

const ItemList: React.FC<ItemListProps> = ({ items, onUpdateLocation, onUpdateName, installationId, selectedItem, setSelectedItem, newName, setNewName, comment, setComment, showDeviceModal, setShowDeviceModal }: ItemListProps) => {
    const {lynxClient} = useGlobalLynxClient();
    const [searchQuery, setSearchQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState<ScannedItem | undefined>();
    const searchRef = useRef<HTMLDivElement>(null);

    const metadataKeys = ['model', 'manufacturer', 'serial_nr', 'eui'];

    const handleDeviceUpdate = async (itemId: string, newDevice: ScannedItem) => {
        try {
            // Update the name locally, does not update device name in lynx!
            onUpdateName(itemId, newDevice.meta.name);

            // Update the device
            const device = items.find(item => item.id === Number(itemId));
            if (device) {
                await lynxClient.updateDevice(newDevice).then(() => {
                    setSelectedItem(null);
                    setSelectedDevice(null);
                    setShowDeviceModal(false);
                }).catch(err => console.error('error updating device:', err));
            }
        } catch (error) {
            console.error('Error updating device:', error);
        }
    };
    
    const mapItems = items.filter(item => item.meta.latitude && item.meta.longitude);

    useEffect(() => {if(!showDeviceModal) setSelectedDevice(undefined);}, [showDeviceModal]);

    useEffect(() => {
        if(!selectedItem) return;
        if(selectedDevice && selectedDevice.id === Number(selectedItem)) return;
        else {
            lynxClient.getDevice(installationId, Number(selectedItem)).then((device: Devicex) => {
                const deviceFunctions: Functionx = items.find((item: ScannedItem)=> item.id === device.id).functions;
                setSelectedDevice({...device, functions: deviceFunctions});
            }).catch(err => console.error(`Failed to get device ${selectedItem} from installation ${installationId}`, err));
        }
    }, [lynxClient, installationId, items, selectedDevice, selectedItem]);

    // Add click outside handler
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchQuery(value);
        setShowSuggestions(value.length > 0);
    };

    const filteredItems = items.filter(item => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return false;
        
        const serialNr = item.meta?.serial_nr?.toString().toLowerCase() || '';
        const eui = item.meta?.eui?.toString().toLowerCase() || '';
        const name = (item.name || '').toLowerCase();
        
        return serialNr.includes(query) || 
               eui.includes(query) || 
               name.includes(query);
    });

    const handleSearchSelect = (itemId: number) => {
        const element = document.getElementById(`device-${itemId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('highlight-device');
            setTimeout(() => {
                element.classList.remove('highlight-device');
            }, 2000);
        }
        setSearchQuery('');
        setShowSuggestions(false);
    };

    const handleExport = async () => {
        try {
            // Get status for all devices
            const exportData = await Promise.all(items.map(async (item) => {
                let lastUpdated = '';
                if (item.functions && item.functions.length > 0) {
                    const topicFilter = item.functions.map((func: Functionx) => func.meta.topic_read);
                    try {
                        const status = await lynxClient.getStatus(installationId, topicFilter);
                        if (status && status.length > 0) {
                            const latestStatus = status.reduce((latest, current) => 
                                current.timestamp > latest.timestamp ? current : latest
                            );
                            lastUpdated = new Date(latestStatus.timestamp * 1000).toLocaleString();
                        }
                    } catch (err) {
                        console.error('Error getting status for device:', err);
                    }
                }

                return {
                    'Name': item.name || 'Unnamed Sensor',
                    'Type': item.content,
                    'DevEUI': item.meta?.eui || '',
                    'Serial Number': item.meta?.serial_nr || '',
                    'Latitude': item.meta?.latitude || '',
                    'Longitude': item.meta?.longitude || '',
                    'Last Updated': lastUpdated
                };
            }));

            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(exportData);

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Devices');

            // Generate Excel file
            XLSX.writeFile(wb, `installation_${installationId}_devices.xlsx`);
        } catch (error) {
            console.error('Error exporting data:', error);
        }
    };

    return (
        <div>
            <MapCardContainer mapItems={mapItems} height={30}/>
            <div className="position-relative mb-2" ref={searchRef}>
                <InputGroup>
                    <Form.Control
                        type="text"
                        placeholder="Search by Name, DevEUI or Serial Number"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onFocus={() => setShowSuggestions(searchQuery.length > 0)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && filteredItems.length > 0) {
                                handleSearchSelect(filteredItems[0].id);
                            }
                        }}
                    />
                    <Button 
                        variant="primary" 
                        onClick={() => {
                            if (filteredItems.length > 0) {
                                handleSearchSelect(filteredItems[0].id);
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faSearch} />
                    </Button>
                </InputGroup>
                {showSuggestions && (
                    <ListGroup 
                        className="position-absolute w-100 shadow-sm" 
                        style={{ 
                            zIndex: 1000, 
                            maxHeight: '200px', 
                            overflowY: 'auto',
                            top: '100%',
                            backgroundColor: 'white'
                        }}
                    >
                        {filteredItems.length > 0 ? (
                            filteredItems.map(item => (
                                <ListGroup.Item 
                                    key={item.id}
                                    action
                                    onClick={() => handleSearchSelect(item.id)}
                                    className="d-flex flex-column py-2"
                                >
                                    <div className="fw-bold">{item.name || 'Unnamed Sensor'}</div>
                                    <small className="text-muted">
                                        {item.meta?.eui && `EUI: ${item.meta.eui}`}
                                        {item.meta?.serial_nr && ` | S/N: ${item.meta.serial_nr}`}
                                    </small>
                                </ListGroup.Item>
                            ))
                        ) : (
                            <ListGroup.Item className="text-muted py-2">
                                No matches found
                            </ListGroup.Item>
                        )}
                    </ListGroup>
                )}
            </div>
            <div className="d-flex justify-content-end mb-3">
                <Button 
                    variant="primary"
                    onClick={() => {
                        handleExport().catch(error => {
                            console.error('Failed to export:', error);
                        });
                    }}
                >
                    <FontAwesomeIcon icon={faFileExport} className="me-2" />
                    Export to Excel
                </Button>
            </div>
            <ListGroup>
                {items.map((item) => (
                    <ListGroup.Item 
                        key={item.id}
                        id={`device-${item.id}`}
                        className="mb-2 border rounded shadow-sm"
                    >
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <h6 className="mb-1">{item.name || 'Unnamed Sensor'}</h6>
                                <p className="text-muted mb-1" style={{ fontSize: '0.875rem' }}>
                                    {item.content}
                                </p>
                                {item.meta && Object.keys(item.meta).length > 0 && (
                                    <div className="mb-2">
                                        {Object.entries(item.meta).map(([key, value]) => {
                                            const val = !value ? 'not available' : value;
                                            return metadataKeys.includes(key) ?
                                                <small key={key} className="d-block text-muted">
                                                    {key === 'manufacturer' 
                                                        ? <><strong>{capitalizeFirstLetter(key)}:</strong> {capitalizeFirstLetter(val as string)}</>
                                                        : key === 'eui'
                                                            ? <><strong>DevEUI:</strong> {val as string}</>
                                                            : key === 'serial_nr'
                                                                ? <><strong>Serial Number:</strong> {value as string}</>
                                                                : `${capitalizeFirstLetter(key)}: ${value}`
                                                    }
                                                </small> :
                                                null;
                                        })}
                                    </div>
                                )}
                                {item.functions && <DeviceFunctions functions={item.functions}/>}
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                        setSelectedItem(item.id);
                                        setShowDeviceModal(true);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                </Button>
                            </div>
                        </div>
                    </ListGroup.Item>
                ))}
            </ListGroup>
            {/* Device Update Modal */}
            <UpdateDeviceModal 
                selectedItem={selectedItem}
                showDeviceModal={showDeviceModal} 
                setShowDeviceModal={setShowDeviceModal}
                name={newName}
                setName={setNewName}
                selectedDevice={selectedDevice}
                setSelectedDevice={setSelectedDevice}
                comment={comment}
                setComment={setComment}
                onUpdateLocation={onUpdateLocation}
                onUpdateDevice={handleDeviceUpdate}
            />
        </div>
    );
};

export default ItemList;

